import type { PaymentEdgesModel } from '@/features/project/projects/_models/payment-edge';
import type { PaymentGroupsModel } from '@/features/project/projects/_models/payment-group';
import { type AmountUnit, BigCalculator, formatValueByUnit } from '@/utils';
import type { z } from 'zod';

export type PaymentNodeData = {
  label: string | number;
  displayCode?: string;
  title: string | undefined;
  paymentCount?: number | null;
  overview?: string | null;
  paymentIndirectCostTextList?: string[] | null;
};

type BaseCalculateAndFormatPaymentTotalType = {
  paymentEdges: z.infer<typeof PaymentEdgesModel>;
  paymentGroups: z.infer<typeof PaymentGroupsModel>;
  unit: AmountUnit;
};
type CalculateAndFormatPaymentTotalType = (
  | {
      type: 'rootNodes';
      totalAmountOfIndirectCosts: number;
    }
  | {
      paymentGroup: z.infer<typeof PaymentGroupsModel>[number];
      type: 'initialNodes';
    }
) &
  BaseCalculateAndFormatPaymentTotalType;

export const calculateAndFormatPaymentTotal = (
  args: CalculateAndFormatPaymentTotalType,
) => {
  const { paymentEdges, paymentGroups, type, unit } = args;
  return formatValueByUnit(
    paymentEdges.reduce(
      (previous, current) => {
        if (type === 'rootNodes' && !current.isConnectedToSourceRoot) {
          return previous;
        }
        if (
          type === 'initialNodes' &&
          current.sourceNodeId !== args.paymentGroup.id
        ) {
          return previous;
        }
        const toPaymentGroup = paymentGroups.find(
          (pg) => pg.id === current.targetNodeId,
        );
        return BigCalculator.add(previous, toPaymentGroup?.totalAmount ?? 0);
      },
      args.type === 'rootNodes' ? args.totalAmountOfIndirectCosts : 0,
    ),
    { operation: 'round', unit, showUnitLabel: true },
  );
};
